import { useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import { Matrix4, Mesh, Vector3, type BufferGeometry, type Group } from 'three';
import type { Task } from '@map/shared';
import { containsPoint, type Territory } from '@map/worldkit';
import { sceneColor } from '../styles/tokens.js';
import { prefersReducedMotion } from '../styles/tokens.js';

/**
 * Finished work, afloat.
 *
 * A completed task leaves the system view and comes down to the ocean of the
 * planet it was closest to. It is not a trophy in a list somewhere: it is out
 * there on the water, moving, for as long as you keep the planet.
 *
 * Three rules make a boat read as a boat rather than a drifting sprite. It
 * only ever sails water — the continents are a wall, not a texture. It keeps
 * clear of every other boat rather than passing through one. And it turns
 * rather than teleports, so the course it takes is its own.
 */
export interface BoatsProps {
  territories: Map<string, Territory>;
  /** Completed tasks belonging to this planet. */
  tasks: Task[];
}

/** Land is drawn at 1.02; a hull sits on the water just under that. */
const SEA_RADIUS = 1.006;
/** How far ahead a boat looks for land or another boat, in radians of arc. */
const LOOKAHEAD = 0.085;
/** No two hulls come closer than this. */
const CLEARANCE = 0.055;
/** Radians per second at cruise. A boat should read as crossing an ocean, not
 *  skimming it: at this rate a lap of the planet is a good twenty minutes. */
const SPEED = 0.0055;
/** How many boats the ocean carries before the oldest stop being drawn. */
const FLEET = 60;

/**
 * A medieval rowing boat, baked to the scene's convention by
 * `scripts/obj-to-glb.py`: bow at +z, up at +y, waterline at y = 0 and two
 * units from stern to bow, so the matrix that puts a boat on the sphere needs
 * to know nothing about the model.
 */
const BOAT_MODEL = '/models/boat.glb';

const POLE = new Vector3(0, 0, 1);
const UP = new Vector3(0, 1, 0);

function hash(id: string, salt: number): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < id.length; i += 1) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

export interface Boat {
  id: string;
  /** Unit vector: where it is on the sphere. */
  at: Vector3;
  /** Unit tangent at `at`: the way it is pointing. */
  forward: Vector3;
  scale: number;
  /** Radians per second, so a fleet never moves as one body. */
  speed: number;
  /** How hard it is currently turning, eased rather than snapped. */
  turn: number;
}

/** Whether a point on the sphere is on any continent rather than at sea. */
export function onLand(shores: readonly Territory[], at: Vector3): boolean {
  for (const shore of shores) if (containsPoint(shore.ring, at)) return true;
  return false;
}

/** Walk `distance` radians along the great circle a boat is pointing down. */
function step(at: Vector3, forward: Vector3, distance: number, into: Vector3): Vector3 {
  return into
    .copy(at)
    .multiplyScalar(Math.cos(distance))
    .addScaledVector(forward, Math.sin(distance))
    .normalize();
}

/** The same heading turned by `angle` in the tangent plane at `at`. */
function turned(at: Vector3, forward: Vector3, angle: number, into: Vector3): Vector3 {
  const side = new Vector3().crossVectors(at, forward).normalize();
  return into
    .copy(forward)
    .multiplyScalar(Math.cos(angle))
    .addScaledVector(side, Math.sin(angle))
    .normalize();
}

const scratchAhead = new Vector3();
const scratchHeading = new Vector3();

/**
 * One boat, one tick.
 *
 * Pulled out of the component because the two rules worth stating — a boat
 * never ends up on land, and never ends up inside another boat — are claims
 * that can be checked rather than looked at.
 *
 * `others` is every other boat's position this tick. The move is only taken
 * if it lands in clear water, so a boat that has sailed into a dead end turns
 * on the spot until it finds a way out instead of beaching itself.
 */
export function advanceBoat(
  boat: Boat,
  shores: readonly Territory[],
  others: readonly Vector3[],
  dt: number,
): void {
  const clear = (heading: Vector3) => {
    step(boat.at, heading, LOOKAHEAD, scratchAhead);
    if (onLand(shores, scratchAhead)) return false;
    for (const them of others) {
      // Chord length is monotonic in the angle between them, so this is the
      // clearance test without an acos per pair per frame.
      if (scratchAhead.distanceToSquared(them) < CLEARANCE * CLEARANCE) return false;
    }
    return true;
  };

  let choice = 0;
  if (!clear(boat.forward)) {
    // Alternating either way, widening: a boat commits to the first gap
    // rather than dithering in the middle of one.
    const fan = [0.3, -0.3, 0.6, -0.6, 1, -1, 1.5, -1.5, 2.4, -2.4];
    choice = fan.find((angle) => clear(turned(boat.at, boat.forward, angle, scratchHeading))) ?? Math.PI;
  }

  boat.turn += (choice - boat.turn) * (1 - Math.exp(-dt * 6));
  if (Math.abs(boat.turn) > 1e-4) {
    turned(boat.at, boat.forward, boat.turn * dt * 3.2, scratchHeading);
    boat.forward.copy(scratchHeading);
  }

  step(boat.at, boat.forward, boat.speed * dt, scratchAhead);
  if (onLand(shores, scratchAhead)) return;
  for (const them of others) {
    if (scratchAhead.distanceToSquared(them) < CLEARANCE * CLEARANCE) return;
  }
  boat.at.copy(scratchAhead);
  // The heading has to be re-tangented after the move, or it drifts off the
  // sphere over thousands of steps.
  boat.forward.addScaledVector(boat.at, -boat.forward.dot(boat.at)).normalize();
}

export function Boats({ territories, tasks }: BoatsProps) {
  const shores = useMemo(
    () => [...territories.values()].filter((t) => t.level === 0),
    [territories],
  );

  // The most recently finished first: an ocean that only ever gains boats
  // would eventually be a traffic jam, so the fleet has a size and the oldest
  // work sails out of sight.
  const fleet = useMemo(() => {
    const done = tasks
      .filter((t) => t.status === 'done')
      .sort((a, b) => (b.completedAt ?? b.createdAt).localeCompare(a.completedAt ?? a.createdAt))
      .slice(0, FLEET);

    const boats: Boat[] = [];
    for (const task of done) {
      // Seeded, so a boat is in the same water every time you open the planet,
      // and pushed off its start until it finds sea.
      const a = hash(task.id, 1);
      const b = hash(task.id, 2);
      let at: Vector3 | null = null;
      for (let attempt = 0; attempt < 24 && !at; attempt += 1) {
        const u = ((a + attempt * 0.137) % 1) * 2 - 1;
        const angle = ((b + attempt * 0.311) % 1) * Math.PI * 2;
        const r = Math.sqrt(Math.max(0, 1 - u * u));
        const candidate = new Vector3(Math.cos(angle) * r, u, Math.sin(angle) * r).normalize();
        if (!onLand(shores, candidate)) at = candidate;
      }
      if (!at) continue;

      // Any tangent will do to start: a great circle through a random point.
      const seed = new Vector3(hash(task.id, 3) - 0.5, hash(task.id, 4) - 0.5, hash(task.id, 5) - 0.5);
      const forward = seed.clone().addScaledVector(at, -seed.dot(at));
      if (forward.lengthSq() < 1e-6) forward.crossVectors(at, Math.abs(at.z) > 0.9 ? UP : POLE);
      forward.normalize();

      boats.push({
        id: task.id,
        at,
        forward,
        scale: 0.016 + hash(task.id, 6) * 0.01,
        speed: SPEED * (0.75 + hash(task.id, 7) * 0.5),
        turn: 0,
      });
    }
    return boats;
  }, [tasks, shores]);

  const groups = useRef<Array<Group | null>>([]);
  const still = useMemo(() => prefersReducedMotion(), []);
  const scratch = useMemo(() => ({ side: new Vector3(), basis: new Matrix4() }), []);

  // Where every boat is this frame, so each one can keep clear of the others
  // without asking the scene graph.
  const places = useRef<Vector3[]>([]);
  useEffect(() => {
    places.current = fleet.map((boat) => boat.at.clone());
  }, [fleet]);

  useFrame((_, delta) => {
    const dt = Math.min(0.05, delta);

    fleet.forEach((boat, index) => {
      if (!still) {
        // Every other boat's position this tick: the one it has to keep clear
        // of. Its own is left out or it would always be blocking itself.
        const others = places.current.filter((_, other) => other !== index);
        advanceBoat(boat, shores, others, dt);
        (places.current[index] ??= new Vector3()).copy(boat.at);
      }

      const group = groups.current[index];
      if (!group) return;
      scratch.side.crossVectors(boat.at, boat.forward).normalize();
      scratch.basis.makeBasis(
        scratch.side.clone().multiplyScalar(boat.scale),
        boat.at.clone().multiplyScalar(boat.scale),
        boat.forward.clone().multiplyScalar(boat.scale),
      );
      scratch.basis.setPosition(
        boat.at.x * SEA_RADIUS,
        boat.at.y * SEA_RADIUS,
        boat.at.z * SEA_RADIUS,
      );
      group.matrix.copy(scratch.basis);
      group.matrixAutoUpdate = false;
      group.matrixWorldNeedsUpdate = true;
    });
  });

  const hull = useMemo(() => sceneColor('trace-settlement', '#b39b72'), []);

  // One geometry for the whole fleet: the boats differ by matrix, not by mesh,
  // and the model is loaded once however many have been finished.
  const { scene } = useGLTF(BOAT_MODEL);
  const geometry = useMemo(() => {
    let found: BufferGeometry | null = null;
    scene.traverse((node) => {
      if (!found && node instanceof Mesh) found = node.geometry as BufferGeometry;
    });
    return found;
  }, [scene]);
  if (!geometry) return null;

  return (
    <group>
      {fleet.map((boat, index) => (
        <group
          key={boat.id}
          ref={(node) => {
            groups.current[index] = node;
          }}
        >
          <mesh geometry={geometry}>
            {/* The model's own materials are dropped in conversion: a boat is
                lit by the planet's light and coloured from the tokens, like
                everything else in the scene. */}
            <meshLambertMaterial color={hull} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

useGLTF.preload(BOAT_MODEL);
